import React, { useState, FC } from 'react';
import { Table, TableBody, TableHead, TableRow, TableCell, Collapse, Box } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Icon from '@mui/material/Icon';
import PersonIcon from '@mui/icons-material/Person';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { SCUIMenuManagementDialog, SCUIMenuRoleDialog, SCUICountryModal, SCUIMenuCopyDialog } from '../../../atomic/molecules';
import Swal from 'sweetalert2';
import { MenuCommand } from '../../../services/command';
import { useTranslation } from 'react-i18next';

interface IProps {
    row: any;
    parent?: any;
    roles?: any;
    refresh?: any;
}
export const SCUIMenuManagementMainRow: FC<IProps> = ({ row, parent, roles, refresh }) => {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);
    const [editedMenu, setEditedMenu] = useState<any>();
    const [openEditModal, setOpenEditModal] = React.useState(false);
    const [openEditRoleModal, setOpenEditRoleModal] = React.useState(false);
    const [openCopyModal, setOpenCopyModal] = React.useState(false);
    const [openCountryModal, setOpenCountryModal] = React.useState(false);
    const [selectedCountryData, setSelectedCountryData] = React.useState<any>(null);
    const onModalEditClose = () => setOpenEditModal(false);
    const onModalRoleClose = () => setOpenEditRoleModal(false);
    const onCountryModalClose = () => {
        setOpenCountryModal(false);
        setSelectedCountryData(null);
    };

    const onModalCopyClose = () => {
        setOpenCopyModal(false);
    };

    const onModalEditOpen = (row: any) => {
        setEditedMenu(row);
        setOpenEditModal(true);
    };

    const onModalCopyOpen = (row: any) => {
        const hasChildren = row?.items?.menuItem && row.items.menuItem.length > 0;
        
        if (hasChildren) {
            setEditedMenu(row);
            setOpenCopyModal(true);
        } else {
            // Direct copy for leaf nodes
            MenuCommand.PostMenuCopy({
                MenuId: row.menuItemId,
                CopyChildren: false,
                CopyRoles: true
            })
            .then(() => {
                Swal.fire(t('menuManagement.copySuccess'), ' ', 'success');
                refresh();
            })
            .catch((error: any) => {
                console.log('Error in copy menu:', error);
                Swal.fire(t('menuManagement.copyFailed'), ' ', 'error');
            });
        }
    };

    const onModalEditRole = (row: any) => {
        setEditedMenu(row);
        setOpenEditRoleModal(true);
    };

    function deleteRecord(row: any) {
        Swal.fire({
            text: t('general.sureText'),
            cancelButtonText: t('cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.delete'),
        }).then((result: any) => {
            if (result.isConfirmed) {
                MenuCommand.DeleteMenu(row.menuItemId)
                    .then(() => {
                        Swal.fire(t('general.deleted'), ' ', 'success');
                        refresh();
                    })
                    .catch((error: any) => {
                        console.log('Hata :>> ', error);
                    })
                    .finally(() => {});
            }
        });
    }
    return (
        <React.Fragment>
            <SCUIMenuManagementDialog
                selectedRow={editedMenu}
                open={openEditModal}
                onClose={onModalEditClose}
                parent={parent}
                refresh={refresh}
            />
            <SCUIMenuRoleDialog
                selectedRow={editedMenu}
                open={openEditRoleModal}
                onClose={onModalRoleClose}
                roles={roles}
            />
            <SCUICountryModal
                open={openCountryModal}
                onClose={onCountryModalClose}
                countryIds={selectedCountryData?.countryIdList || []}
            />
            <SCUIMenuCopyDialog
                selectedRow={editedMenu}
                open={openCopyModal}
                onClose={onModalCopyClose}
                refresh={refresh}
            />

            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }} key={row.menuItemId}>
                <TableCell className="mainMenuHeader">
                    <IconButton size="large" onClick={() => setOpened(!opened)}>
                        {opened ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>

                <TableCell component="th" scope="row" className="mainMenuHeader">
                    {row.text}
                </TableCell>

                <TableCell align="center" className="mainMenuHeader">
                    {row.orderNo}
                </TableCell>

                <TableCell align="center" className="mainMenuHeader">
                    <Icon>{row.iconName ? row.iconName : '-'}</Icon>
                </TableCell>

                <TableCell align="center" className="mainMenuHeader">
                    {row.countryIdList && row.countryIdList.length > 0 ? (
                        <Box
                            onClick={() => {
                                setSelectedCountryData(row);
                                setOpenCountryModal(true);
                            }}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                backgroundColor: '#1976d2',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: '#1565c0',
                                    transform: 'scale(1.05)',
                                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                                },
                            }}
                        >
                            {row.countryIdList.length}
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                backgroundColor: '#e0e0e0',
                                color: '#757575',
                                fontSize: '14px',
                                fontWeight: 600,
                            }}
                        >
                            0
                        </Box>
                    )}
                </TableCell>

                <TableCell align="center" className="mainMenuHeader">
                    <IconButton
                        onClick={() => onModalEditRole(row)}
                        className="mainMenuIcon"
                        sx={{ backgroundColor: '#03a9f4', color: 'white' }}
                    >
                        <PersonIcon />
                    </IconButton>
                </TableCell>

                <TableCell align="center" className="mainMenuHeader">
                    <IconButton
                        onClick={() => deleteRecord(row)}
                        className="mainMenuIcon"
                        sx={{ backgroundColor: '#03a9f4', color: 'white' }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </TableCell>
                <TableCell align="center" className="mainMenuHeader">
                    <IconButton
                        onClick={() => onModalEditOpen(row)}
                        className="mainMenuIcon"
                        sx={{ backgroundColor: '#03a9f4', color: 'white' }}
                    >
                        <EditIcon />
                    </IconButton>
                </TableCell>
                <TableCell align="center" className="mainMenuHeader">
                    <IconButton
                        onClick={() => onModalCopyOpen(row)}
                        className="mainMenuIcon"
                        sx={{ backgroundColor: '#03a9f4', color: 'white' }}
                    >
                        <ContentCopyIcon />
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow key={'id' + row.menuItemId}>
                <TableCell
                    style={{
                        paddingBottom: 0,
                        paddingTop: 0,
                        backgroundColor: '#C8C8C8',
                    }}
                    colSpan={10}
                >
                    <Collapse in={opened} timeout="auto" unmountOnExit>
                        <Table>
                            <TableHead>
                                <TableRow key={'l' + row.menuItemId}>
                                    <TableCell>{t('general.text')}</TableCell>
                                    <TableCell>URL</TableCell>
                                    <TableCell>{t('general.orderSira')}</TableCell>
                                    <TableCell>{t('general.icon')}</TableCell>
                                    <TableCell>{t('menuManagement.countryColumn')}</TableCell>
                                    <TableCell>{t('general.role')}</TableCell>
                                    <TableCell>{t('general.delete')}</TableCell>
                                    <TableCell>{t('general.edit')}</TableCell>
                                    <TableCell>{t('menuManagement.copy')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {row.items?.menuItem.map((menuItem: any) => (
                                    <TableRow key={menuItem.menuItemId}>
                                        <TableCell>{menuItem.text}</TableCell>
                                        <TableCell>{menuItem.navigateViewUrl}</TableCell>
                                        <TableCell>{menuItem.orderNo}</TableCell>
                                        <TableCell>
                                            <Icon>{menuItem.iconName ? menuItem.iconName : '-'}</Icon>
                                        </TableCell>
                                        <TableCell>
                                            {menuItem.countryIdList && menuItem.countryIdList.length > 0 ? (
                                                <Box
                                                    onClick={() => {
                                                        setSelectedCountryData(menuItem);
                                                        setOpenCountryModal(true);
                                                    }}
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: 28,
                                                        height: 28,
                                                        borderRadius: '50%',
                                                        backgroundColor: '#1976d2',
                                                        color: 'white',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            backgroundColor: '#1565c0',
                                                            transform: 'scale(1.05)',
                                                            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                                                        },
                                                    }}
                                                >
                                                    {menuItem.countryIdList.length}
                                                </Box>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: 28,
                                                        height: 28,
                                                        borderRadius: '50%',
                                                        backgroundColor: '#e0e0e0',
                                                        color: '#757575',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    0
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                onClick={() => onModalEditRole(menuItem)}
                                                className="mainMenuIcon"
                                                sx={{ backgroundColor: '#03a9f4', color: 'white' }}
                                            >
                                                <PersonIcon />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                onClick={() => deleteRecord(menuItem)}
                                                className="mainMenuIcon"
                                                sx={{ backgroundColor: '#03a9f4', color: 'white' }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                onClick={() => onModalEditOpen(menuItem)}
                                                className="mainMenuIcon"
                                                sx={{ backgroundColor: '#03a9f4', color: 'white' }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                onClick={() => onModalCopyOpen(menuItem)}
                                                className="mainMenuIcon"
                                                sx={{ backgroundColor: '#03a9f4', color: 'white' }}
                                            >
                                                <ContentCopyIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};
